import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { useAutoRefresh } from '../hooks/useAutoRefresh';

// 커스텀 툴팁 (컴포넌트 외부에 선언하여 리렌더링 방지)
const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div style={{
        backgroundColor: '#1B1D20',
        border: '1px solid #374151',
        borderRadius: '6px',
        padding: '8px 12px',
        color: '#E5E7EB',
        fontSize: '13px'
      }}>
        <div style={{ fontWeight: 600, marginBottom: '4px' }}>{`${data.hour}시`}</div>
        <div style={{ color: '#60A5FA' }}>{'생산량:'} <span style={{ color: '#FBBF24', fontWeight: 600 }}>{data.count.toLocaleString()}</span></div>
      </div>
    );
  }
  return null;
};

// 바 위 숫자 라벨 렌더러
const renderBarLabel = (props) => {
  const { x, y, width, value } = props;
  if (!value || value <= 0) return null;
  return (
    <text
      x={x + width / 2}
      y={y - 8}
      textAnchor="middle"
      fill="#E5E7EB"
      fontSize={11}
    >
      {value}
    </text>
  );
};

/**
 * 데이터 값을 부드럽게 보간하는 훅
 * Recharts 내장 애니메이션 대신 데이터 자체를 애니메이션
 */
/* demo 전용 더미 생성기. 백엔드가 없으므로 설비/날짜로 결정적인 시간대별 생산량을 만든다.
   같은 설비·같은 날짜면 항상 같은 값이 나와야 화면이 깜빡이지 않는다. */
const buildDummyPph = (equipmentId, dateStr) => {
  let seed = 0;
  const key = String(equipmentId || '') + String(dateStr || '');
  for (let i = 0; i < key.length; i += 1) seed = (seed * 31 + key.charCodeAt(i)) >>> 0;
  const rand = (salt) => {
    let x = (Math.imul(seed + 1, 2654435761) ^ Math.imul(salt, 40503)) >>> 0;
    x ^= x >>> 13;
    x = Math.imul(x, 1274126177) >>> 0;
    x ^= x >>> 16;
    return (x >>> 0) / 4294967296;
  };

  const today = new Date();
  const isToday = dateStr === `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  const lastHour = isToday ? today.getHours() : 23;

  const hourlyData = [];
  let dailyTotal = 0;
  for (let h = 0; h <= 23; h += 1) {
    let count = 0;
    if (h <= lastHour) {
      if (h === 12) count = Math.round(60 + rand(h) * 20);                  // 점심 감산
      else if (h >= 8 && h <= 17) count = Math.round(180 + rand(h) * 40);   // 주간 정상 가동
      else if (h >= 6 && h < 8) count = Math.round(90 + rand(h) * 40);      // 가동 준비
      else if (h > 17 && h <= 20) count = Math.round(120 + rand(h) * 40);   // 잔업
      else count = 0;                                                       // 야간 정지
      if (isToday && h === lastHour) count = Math.round(count * ((today.getMinutes() + 1) / 60));
    }
    hourlyData.push({ hour: h, count });
    dailyTotal += count;
  }
  return { hourlyData, dailyTotal };
};

const useAnimatedChartData = (targetData, duration = 500) => {
  const [displayData, setDisplayData] = useState(targetData);
  const prevCountsRef = useRef(null);
  const animRef = useRef(null);

  useEffect(() => {
    // 이전 값 캡처
    const prevCounts = prevCountsRef.current;

    // 최초 로드거나 길이가 다르면 즉시 반영
    if (!prevCounts || prevCounts.length !== targetData.length) {
      prevCountsRef.current = targetData.map(d => d.count);
      setDisplayData(targetData);
      return;
    }

    const startValues = prevCounts;
    const endValues = targetData.map(d => d.count);

    // 값이 동일하면 애니메이션 불필요
    const hasChange = startValues.some((v, i) => v !== endValues[i]);
    if (!hasChange) {
      setDisplayData(targetData);
      return;
    }

    const startTime = performance.now();

    const animate = (now) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3);

      const interpolated = targetData.map((d, i) => ({
        ...d,
        count: Math.round(startValues[i] + (endValues[i] - startValues[i]) * eased)
      }));

      setDisplayData(interpolated);

      if (progress < 1) {
        animRef.current = requestAnimationFrame(animate);
      } else {
        // 애니메이션 완료 시 정확한 최종값 보장
        prevCountsRef.current = endValues;
      }
    };

    if (animRef.current) cancelAnimationFrame(animRef.current);
    animRef.current = requestAnimationFrame(animate);

    return () => {
      if (animRef.current) cancelAnimationFrame(animRef.current);
      // cleanup 시 최종값으로 업데이트
      prevCountsRef.current = endValues;
    };
  }, [targetData, duration]);

  return displayData;
};

/**
 * PPH(시간당 생산량) 탭 컴포넌트
 * 시간대별 생산량을 바 차트로 표시
 */
const MonitoringPPH = ({ equipmentId }) => {
  const todayStr = new Date().toISOString().split('T')[0];
  const [selectedDate, setSelectedDate] = useState(todayStr);
  const [chartData, setChartData] = useState([]);
  const [dailyTotal, setDailyTotal] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const selectedDateRef = useRef(selectedDate);
  const equipmentIdRef = useRef(equipmentId);
  const prevDataKeyRef = useRef('');

  // 데이터 값을 부드럽게 보간
  const displayData = useAnimatedChartData(chartData);

  // 데이터가 실제로 변경됐을 때만 state 업데이트
  const updateChartIfChanged = useCallback((hourly, total) => {
    const newKey = hourly.map(h => `${h.hour}:${h.count}`).join(',');
    if (newKey !== prevDataKeyRef.current) {
      prevDataKeyRef.current = newKey;
      setChartData(hourly.map(h => ({ hour: h.hour, count: h.count })));
      setDailyTotal(total);
    }
  }, []);

  // ref를 항상 최신값으로 유지
  useEffect(() => {
    selectedDateRef.current = selectedDate;
    prevDataKeyRef.current = '';
  }, [selectedDate]);

  useEffect(() => {
    equipmentIdRef.current = equipmentId;
    prevDataKeyRef.current = '';
  }, [equipmentId]);

  // 자동갱신용 (ref 기반)
  const fetchPphSilent = useCallback(() => {
    const eqId = equipmentIdRef.current;
    const date = selectedDateRef.current;
    if (!eqId) return;
    const dummy = buildDummyPph(eqId, date);
    updateChartIfChanged(dummy.hourlyData, dummy.dailyTotal);
  }, [updateChartIfChanged]);

  // equipmentId 또는 selectedDate 변경 시 fetch (불필요한 리렌더 없음)
  useEffect(() => {
    if (!equipmentId) return;
    setLoaded(false);
    const dummy = buildDummyPph(equipmentId, selectedDate);
    updateChartIfChanged(dummy.hourlyData, dummy.dailyTotal);
    setLoaded(true);
  }, [equipmentId, selectedDate, updateChartIfChanged]);

  useAutoRefresh(fetchPphSilent);

  const maxCount = displayData.length > 0 ? Math.max(...displayData.map(d => d.count), 1) : 500;
  const yMax = useMemo(() => Math.ceil(maxCount / 50) * 50 + 50, [maxCount]);

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: '3px',
      height: '100%'
    }}>
      {/* 설정 카드 */}
      <div style={{
        backgroundColor: '#21252b',
        border: '1px solid #1B1D20',
        padding: '4px 12px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <label style={{ color: '#9CA3AF', fontSize: '13px', whiteSpace: 'nowrap' }}>{'조회일자 :'}</label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              style={{
                padding: '6px 10px',
                backgroundColor: '#1B1D20',
                border: '1px solid #374151',
                borderRadius: '4px',
                color: '#E5E7EB',
                fontSize: '13px',
                cursor: 'pointer',
                colorScheme: 'dark'
              }}
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ color: '#9CA3AF', fontSize: '13px', whiteSpace: 'nowrap' }}>{'일일총생산량'}</span>
            <span style={{
              padding: '5px 16px',
              backgroundColor: '#1B1D20',
              border: '1px solid #374151',
              borderRadius: '4px',
              color: '#FBBF24',
              fontSize: '14px',
              fontWeight: 700,
              minWidth: '80px',
              textAlign: 'center'
            }}>
              {!loaded ? '-' : dailyTotal.toLocaleString()}
            </span>
          </div>
        </div>
      </div>

      {/* 차트 카드 */}
      <div style={{
        backgroundColor: '#21252b',
        border: '1px solid #1B1D20',
        padding: '16px',
        flex: 1,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {chartData.length === 0 && loaded ? (
          <div style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#6B7280',
            fontSize: '14px'
          }}>
            {'데이터가 없습니다'}
          </div>
        ) : (
          <div style={{ flex: 1, minHeight: 0 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={displayData}
                margin={{ top: 30, right: 20, left: 20, bottom: 30 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#374151"
                  vertical={false}
                />
                <XAxis
                  dataKey="hour"
                  tick={{ fill: '#9CA3AF', fontSize: 12 }}
                  axisLine={{ stroke: '#4B5563' }}
                  tickLine={{ stroke: '#4B5563' }}
                  label={{
                    value: '시간',
                    position: 'insideBottom',
                    offset: -15,
                    fill: '#9CA3AF',
                    fontSize: 13
                  }}
                />
                <YAxis
                  domain={[0, yMax]}
                  tick={{ fill: '#9CA3AF', fontSize: 12 }}
                  axisLine={{ stroke: '#4B5563' }}
                  tickLine={{ stroke: '#4B5563' }}
                  label={{
                    value: '생산량',
                    angle: -90,
                    position: 'insideLeft',
                    offset: -5,
                    fill: '#9CA3AF',
                    fontSize: 13
                  }}
                />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(96, 165, 250, 0.1)' }} />
                <Bar
                  dataKey="count"
                  radius={[3, 3, 0, 0]}
                  maxBarSize={35}
                  isAnimationActive={false}
                  label={renderBarLabel}
                >
                  {displayData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={entry.count > 0 ? '#3B82F6' : '#1F2937'}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
};

export default MonitoringPPH;
